import React from 'react';
import { Button, MenuItem, TextField } from '@mui/material';
import {
	GridColDef,
	GridColumns,
	GridActionsCellItem,
	GridRowId,
	GridApiRef,
	GridRenderEditCellParams,
} from '@mui/x-data-grid-pro';
import { TFunction } from 'i18next';
import { RiPencilLine } from 'react-icons/ri';
import { stringEnum } from 'common/utils/objects';
import IconButton from '@mui/material/IconButton';
import { RiInformationLine } from 'react-icons/ri';
import CustomTooltip from 'components/CustomTooltip';
import { SkuItem } from 'common/modules/inventory';
import { localizedType } from 'views/Inventory/utils';
import { EnumKeys } from 'common/types';
import ColorCodedTypography from 'components/ColorCodedTypography';

export const FixedSkuColumns = stringEnum([
	'sku_code',
	'sku_name',
	'type',
	'products',
	'articles',
	'in_stock',
	'for_sale',
	'for_rent',
	'committed',
]);
// These are fields that are added to the SKU row (to use as data attributes), but are not rendered in the SKU table
export const HiddenSkuColumns = stringEnum(['quantity']);
export const StaticSkuColumns = stringEnum(['actions']);

const {
	sku_code,
	sku_name,
	type,
	products,
	articles,
	in_stock,
	for_sale,
	for_rent,
	committed,
} = FixedSkuColumns;
const { quantity } = HiddenSkuColumns;

const typeValueOptions = ['single', 'bulk'] as const;

export interface SkuColumnFieldsWithTypes {
	[sku_code]: string;
	[sku_name]: string;
	[type]: SkuItem['itemType'];
	[products]: number;
	[articles]: number;
	[quantity]: number;
	[in_stock]: number;
	[for_sale]: number;
	[for_rent]: number;
	[committed]: number;
}

type FixedSkuColumnField = EnumKeys<typeof FixedSkuColumns>;

interface ColumnParams extends GridColDef {
	field: FixedSkuColumnField;
}

export const getColumnName = (label: string, t: TFunction) => {
	const columnNameMapping: { [key in FixedSkuColumnField]: string } = {
		[sku_code]: t('inventory.columns.skuCOde', 'SKU Code'),
		[sku_name]: t('inventory.columns.skuName', 'SKU Name'),
		[type]: t('inventory.columns.type', 'Type'),
		[products]: t('inventory.columns.products', 'Products'),
		[articles]: t('inventory.columns.articles', 'Articles'),
		[in_stock]: t('inventory.columns.inStock', 'In stock'),
		[for_sale]: t('inventory.columns.forSale', 'For sale'),
		[for_rent]: t('inventoty.columns.forRent', 'For rent'),
		[committed]: t('inventory.columns.committed', 'Committed'),
	};
	return columnNameMapping[label] ?? label;
};

const getEditableCell = (params: GridRenderEditCellParams) => {
	const { id, value, api, field } = params;
	return (
		<TextField
			id="editable-cell"
			size="small"
			sx={{
				backgroundColor: (theme) => theme.palette.background.paper,
			}}
			value={value}
			onChange={(event) => {
				api.setEditCellValue({ id, field, value: event.target.value }, event);
			}}
		/>
	);
};

const getEditableTypeCell = (params: GridRenderEditCellParams, t: TFunction) => {
	const { id, value, api, field, row } = params;
	const skuQuantity = row.quantity;
	const typeEditDisabled = skuQuantity !== 0;
	const options = typeValueOptions;
	return typeEditDisabled ? (
		localizedType(value as any, t)
	) : (
		<TextField
			size="small"
			select
			sx={{
				backgroundColor: (theme) => theme.palette.background.paper,
			}}
			value={value}
			onChange={(event) => {
				api.setEditCellValue({ id, field, value: event.target.value }, event);
			}}
		>
			{options.map((option) => (
				<MenuItem value={option}>{localizedType(option, t)}</MenuItem>
			))}
		</TextField>
	);
};

export const getFixedColumns = (t: TFunction): ColumnParams[] => [
	{
		field: sku_code,
		headerName: getColumnName(sku_code, t),
		width: 250,
		editable: true,
		renderEditCell: getEditableCell,
	},
	{
		field: sku_name,
		headerName: getColumnName(sku_name, t),
		width: 350,
		editable: true,
		renderEditCell: getEditableCell,
	},
	{
		field: type,
		headerName: getColumnName(type, t),
		editable: true,
		type: 'singleSelect',
		valueOptions: typeValueOptions.slice(),
		renderCell: (params) => localizedType(params.value, t),
		renderEditCell: (params) => getEditableTypeCell(params, t),
		flex: 1,
		minWidth: 100,
	},
	{
		field: articles,
		headerName: getColumnName(articles, t),
		type: 'number',
		flex: 1,
		minWidth: 150,
	},
	{
		field: in_stock,
		headerName: getColumnName(in_stock, t),
		type: 'number',
		flex: 1,
		minWidth: 150,
		renderCell: (params) => (
			<ColorCodedTypography fontSize="1.4rem" value={params.value as number} />
		),
	},
	{
		field: for_rent,
		headerName: getColumnName(for_rent, t),
		type: 'number',
		flex: 1,
		minWidth: 150,
		renderCell: (params) => (
			<ColorCodedTypography fontSize="1.4rem" value={params.value as number} />
		),
	},
	{
		field: for_sale,
		headerName: getColumnName(for_sale, t),
		type: 'number',
		flex: 1,
		minWidth: 150,
		renderCell: (params) => (
			<ColorCodedTypography fontSize="1.4rem" value={params.value as number} />
		),
	},
	{
		field: committed,
		headerName: getColumnName(committed, t),
		type: 'number',
		flex: 1,
		minWidth: 150,
		renderCell: (params) => (
			<ColorCodedTypography fontSize="1.4rem" value={params.value as number} />
		),
	},
	{
		field: products,
		headerName: getColumnName(products, t),
		type: 'number',
		flex: 1,
		minWidth: 150,
	},
];

export type ActionCallback = (
	id: GridRowId,
) => (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;

interface ActionsColumnProps {
	apiRef: GridApiRef;
	activeRowId: string | null;
	disabledRowIds: string[];
	actions: {
		onSave: ActionCallback;
		onCancel: ActionCallback;
		onEdit: ActionCallback;
	};
	t: TFunction;
}

export const getActionsColumn = (props: ActionsColumnProps): GridColumns => {
	const { apiRef, activeRowId, disabledRowIds, actions, t } = props;
	const { onSave, onCancel, onEdit } = actions;
	return [
		{
			field: StaticSkuColumns.actions,
			headerName: '',
			type: StaticSkuColumns.actions,
			align: 'right',
			resizable: false,
			minWidth: 250,
			headerClassName: 'hide-column-separator',
			getActions: ({ id }) => {
				const isRowEditingDisabled = disabledRowIds.includes(id.toString());
				const isDisabled = !!activeRowId && id !== activeRowId;
				const isInEditMode = apiRef.current.getRowMode(id) === 'edit';

				if (isInEditMode) {
					return [
						<Button
							variant="outlined"
							color="primary"
							size="small"
							onClick={onCancel(id)}
							sx={{
								backgroundColor: (theme) => theme.palette.background.paper,
							}}
						>
							{t('common:actions.cancel')}
						</Button>,
						<Button variant="contained" color="primary" size="small" onClick={onSave(id)}>
							{t('common:actions.save')}
						</Button>,
					];
				}

				return [
					...(isRowEditingDisabled
						? [
								<CustomTooltip
									title={
										t(
											'inventory.sku.table.rowDisabledExplanation',
											`This SKU is in use in stores that you don't have access to.`,
										) ?? ''
									}
								>
									<IconButton size="small">
										<RiInformationLine />
									</IconButton>
								</CustomTooltip>,
						  ]
						: []),

					<GridActionsCellItem
						icon={<RiPencilLine />}
						label={t('common:actions.edit')}
						onClick={onEdit(id)}
						color="inherit"
						disabled={isDisabled}
					/>,
				];
			},
		},
	];
};
